/**
 * The single sign-on protocol between estimating's API and the apps that share a
 * brand's sign-in: estimating's web app and the brand's shop management system,
 * ImEX Online or Rome Online (D-060). The API and the client both import it from
 * here, so they can't drift apart.
 */

/** The API's single sign-on routes. The session cookie is sent to nothing else. */
export const SSO_PATH = '/v1/sso';
export const SSO_SESSION_PATH = `${SSO_PATH}/session`;
export const SSO_TOKEN_PATH = `${SSO_PATH}/token`;

/** A shared sign-in lasts as long as Firebase lets a session cookie last, unless an app renews it. */
export const SSO_SESSION_MAX_AGE_SECONDS = 14 * 24 * 60 * 60;

/** The marker's value once someone has signed out on purpose: every app then signs out too. */
export const SIGNED_OUT_MARKER = 'out';

export interface SsoCookieNames {
  /** The Firebase session cookie: HttpOnly, and sent only to the API's single sign-on routes. */
  readonly session: string;
  /**
   * Readable by the apps' scripts and holding no secret: it tells them that a shared
   * sign-in exists and which one, or that someone signed out.
   */
  readonly marker: string;
}

export interface SsoCookieScope {
  /** The brand's Firebase project. */
  readonly projectId: string;
  /** The domain the cookies are set on, such as imex.online; null for the API's host alone. */
  readonly cookieDomain: string | null;
  /** Over HTTPS; the names then carry the `__Secure-` prefix, so a plain HTTP page can't plant one. */
  readonly secure: boolean;
}

/**
 * Named after the Firebase project and the domain. A production domain's cookies also
 * reach its staging hosts (imex.online's reach test.imex.online), and a staging platform
 * may share production's Firebase project, as Rome Online's does, so the domain is what
 * keeps the two apart.
 */
export function ssoCookieNames({
  projectId,
  cookieDomain,
  secure,
}: SsoCookieScope): SsoCookieNames {
  const prefix = secure ? '__Secure-' : '';
  const name = `${prefix}sso_${projectId}${cookieDomain === null ? '' : `_${cookieDomain}`}`;
  return { session: name, marker: `${name}_marker` };
}

/** `POST /v1/sso/session`: a shared sign-in started from an app's own. */
export interface SsoSessionStarted {
  /** The marker cookie's new value. */
  readonly marker: string;
}

/** `POST /v1/sso/token`: what an app signs in with to join the shared sign-in. */
export interface SsoSessionJoined extends SsoSessionStarted {
  readonly uid: string;
  /** A Firebase custom token for `uid`, for `signInWithCustomToken`. Valid for an hour. */
  readonly customToken: string;
}
